import { useState, useRef, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  MapIcon,
  ChartBarIcon,
  BellIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  GlobeAltIcon,
  PlusIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ClockIcon,
  MapPinIcon,
  ShieldExclamationIcon,
  CloudIcon,
  CalendarDaysIcon,
  WrenchScrewdriverIcon,
  BeakerIcon,
  CurrencyDollarIcon,
  LanguageIcon,
  UserGroupIcon,
  ShareIcon,
  WrenchIcon,
  SunIcon,
  BellAlertIcon,
  BanknotesIcon,
  BookOpenIcon,
  GlobeEuropeAfricaIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import ThemeToggle from './ThemeToggle';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'My Farms', href: '/farms', icon: GlobeAltIcon },
  { name: 'Map View', href: '/map', icon: MapIcon },
  { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
  { name: 'History', href: '/history', icon: ClockIcon },
  { name: 'VRA Maps', href: '/vra', icon: MapPinIcon },
  { name: 'Diseases', href: '/diseases', icon: ShieldExclamationIcon },
  { name: 'Irrigation', href: '/irrigation', icon: CloudIcon },
  { name: 'Calendar', href: '/calendar', icon: CalendarDaysIcon },
  { name: 'Soil Health', href: '/soil-health', icon: BeakerIcon },
  { name: 'Market Prices', href: '/market', icon: CurrencyDollarIcon },
  { name: 'Cooperatives', href: '/cooperatives', icon: UserGroupIcon },
  { name: 'Farm Sharing', href: '/sharing', icon: ShareIcon },
  { name: 'Equipment', href: '/equipment', icon: WrenchIcon },
  { name: 'Weather', href: '/weather', icon: SunIcon },
  { name: 'Notifications', href: '/notifications', icon: BellAlertIcon },
  { name: 'Finance', href: '/finance', icon: BanknotesIcon },
  { name: 'Geo Tools', href: '/geo-tools', icon: GlobeEuropeAfricaIcon },
  { name: 'Knowledge Base', href: '/knowledge', icon: BookOpenIcon },
  { name: 'Alerts', href: '/alerts', icon: BellIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isConnected, notifications } = useWebSocket();
  const { language, setLanguage } = useLanguage();
  const userMenuRef = useRef<HTMLDivElement>(null);

  const isActive = (href: string) => location.pathname.startsWith(href);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch {
      toast.error('Logout failed');
    }
  };

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    return user.name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64
          bg-gradient-to-b from-kenagri-900 via-kenagri-800 to-kenagri-900
          transform transition-transform duration-300 ease-out
          lg:translate-x-0 lg:static
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          scrollbar-dark overflow-y-auto
        `}
      >
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 pattern-grid opacity-30 pointer-events-none" />

        {/* Logo */}
        <div className="relative flex h-16 items-center justify-between px-5 border-b border-white/10">
          <Link to="/" className="flex items-center group">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-kenagri-400 to-kenagri-600 shadow-glow-green">
              <span className="text-lg">🛰️</span>
            </div>
            <span className="ml-3 text-xl font-bold text-white tracking-tight group-hover:text-kenagri-200 transition-colors">
              KENAGRI
            </span>
          </Link>
          <button
            className="lg:hidden p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="relative mt-6 px-3 space-y-1">
          {navigation.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`
                  group flex items-center px-3 py-2.5 rounded-xl text-sm font-medium
                  transition-all duration-200
                  ${active
                    ? 'bg-white/20 text-white shadow-soft'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }
                `}
              >
                <item.icon className={`h-5 w-5 mr-3 transition-transform duration-200 group-hover:scale-110 ${active ? 'text-kenagri-300' : ''}`} />
                <span className="flex-1">{item.name}</span>
                {active && (
                  <span className="w-1.5 h-1.5 rounded-full bg-kenagri-400 animate-pulse-soft" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Add Farm Button */}
        <div className="relative mt-8 px-4">
          <Link
            to="/farms/new"
            className="
              flex items-center justify-center w-full px-4 py-3
              bg-gradient-to-r from-kenagri-500 to-kenagri-600
              hover:from-kenagri-400 hover:to-kenagri-500
              text-white rounded-xl text-sm font-medium
              shadow-soft hover:shadow-soft-md
              hover:-translate-y-0.5
              transition-all duration-200
            "
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add New Farm
          </Link>
        </div>

        {/* Footer */}
        <div className="absolute bottom-4 left-0 right-0 px-5">
          <div className="pt-4 border-t border-white/10">
            <p className="text-white/60 text-xs font-medium">KENAGRI v0.1.0</p>
            <p className="text-white/40 text-xs mt-0.5">Satellite Crop Monitoring</p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 glass-strong sticky top-0 z-30 border-b border-gray-200/50 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            <h1 className="text-lg font-semibold text-gray-900">
              {navigation.find((n) => isActive(n.href))?.name || 'KENAGRI'}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {/* WebSocket connection status */}
            <div
              className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-lg"
              title={isConnected ? 'Real-time updates connected' : 'Connecting to real-time updates...'}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  isConnected
                    ? 'bg-green-500 animate-pulse'
                    : 'bg-yellow-500 animate-ping'
                }`}
              />
              <span className="text-xs text-gray-500">
                {isConnected ? 'Live' : 'Connecting'}
              </span>
            </div>

            {/* Theme toggle */}
            <ThemeToggle />

            {/* Language switcher */}
            <button
              onClick={() => setLanguage(language === 'en' ? 'sw' : 'en')}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              title={language === 'en' ? 'Switch to Swahili' : 'Badilisha kwa Kiingereza'}
            >
              <LanguageIcon className="h-4 w-4" />
              <span className="text-xs font-medium uppercase">{language}</span>
            </button>

            {/* Notification bell */}
            <Link
              to="/alerts"
              className="relative p-2.5 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <BellIcon className="h-5 w-5" />
              {notifications.length > 0 ? (
                <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full ring-2 ring-white flex items-center justify-center text-[10px] font-bold text-white">
                  {notifications.length > 99 ? '99+' : notifications.length}
                </span>
              ) : (
                <span className="absolute top-2 right-2 h-2 w-2 bg-gray-300 rounded-full ring-2 ring-white" />
              )}
            </Link>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2.5 p-1.5 pl-1.5 pr-3 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-kenagri-500 to-kenagri-600 flex items-center justify-center text-white font-medium text-sm shadow-soft">
                  {getUserInitials()}
                </div>
                <span className="hidden md:block text-sm font-medium text-gray-700">
                  {user?.name || 'User'}
                </span>
              </button>

              {/* Dropdown Menu */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-60 glass-strong rounded-xl shadow-soft-xl border border-gray-200/50 py-1 z-50 animate-scale-in origin-top-right">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                    <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                  </div>

                  <div className="py-1">
                    <Link
                      to="/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <UserCircleIcon className="h-5 w-5 mr-3 text-gray-400" />
                      Profile Settings
                    </Link>
                    {user?.role === 'admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <WrenchScrewdriverIcon className="h-5 w-5 mr-3 text-gray-400" />
                        Admin Panel
                      </Link>
                    )}
                  </div>

                  <div className="border-t border-gray-100 py-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
